import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, LogOut, Download, FileText, Inbox } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import DashboardTab from "../components/admin/DashboardTab";
import NewsTab from "../components/admin/NewsTab";
import EventsTab from "../components/admin/EventsTab";
import ProjectsTab from "../components/admin/ProjectsTab";
import NewsletterTab from "../components/admin/NewsletterTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORM_TYPES = ["network", "partner", "support", "project", "contact"];

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("ghp-admin-token")}` });

const inputCls =
  "w-full border border-graphite/20 bg-white px-4 py-3 text-sm text-graphite focus:outline-none focus:border-forest transition-colors duration-200";
const labelCls = "overline-mono mb-2 block text-graphite-soft";

const AdminPage = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [subs, setSubs] = useState([]);
  const [news, setNews] = useState([]);
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [docForm, setDocForm] = useState({ title_fr: "", title_en: "", description_fr: "", description_en: "", category: "statuts" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, n, d] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers: authHeader() }),
        axios.get(`${API}/admin/news`, { headers: authHeader() }),
        axios.get(`${API}/admin/documents`, { headers: authHeader() }),
      ]);
      setSubs(s.data);
      setNews(n.data);
      setDocs(d.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("ghp-admin-token");
        navigate("/admin/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("ghp-admin-token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

  const logout = () => {
    localStorage.removeItem("ghp-admin-token");
    navigate("/admin/login");
  };

  const del = async (path, id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try {
      await axios.delete(`${API}/admin/${path}/${id}`, { headers: authHeader() });
      toast.success(t("admin.deleted"));
      load();
    } catch {
      toast.error(t("forms.error"));
    }
  };

  const submitDoc = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSaving(true);
    const fd = new FormData();
    Object.entries(docForm).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/documents`, fd, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } });
      toast.success(t("admin.docSaved"));
      setDocForm({ title_fr: "", title_en: "", description_fr: "", description_en: "", category: "statuts" });
      setFile(null);
      load();
    } catch {
      toast.error(t("forms.error"));
    }
    setSaving(false);
  };

  const tabs = [
    ["dashboard", t("admin.tabDashboard"), null],
    ["submissions", t("admin.tabSubmissions"), subs.length],
    ["news", t("admin.tabNews"), news.length],
    ["events", t("admin.tabEvents"), null],
    ["projects", t("admin.tabProjects"), null],
    ["docs", t("admin.tabDocs"), docs.length],
    ["newsletter", t("admin.tabNewsletter"), null],
  ];

  const filteredSubs = filter === "all" ? subs : subs.filter((s) => s.type === filter);
  const docKeys = t("ressourcesKeys");
  const docCats = t("ressources.categories");

  return (
    <div className="min-h-screen bg-bone pt-16" data-testid="admin-dashboard">
      <div className="border-b border-graphite/10 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-6">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="" className="h-10 w-10 object-contain" />
            <div>
              <p className="overline-mono text-forest">Green Horizon Project</p>
              <h1 className="font-display text-2xl font-light tracking-tight text-graphite" data-testid="admin-dashboard-title">
                {t("admin.dashboard")}
              </h1>
            </div>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 border border-graphite/20 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-graphite-soft transition-colors hover:border-destructive hover:text-destructive"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-3.5 w-3.5" />
            {t("admin.logout")}
          </button>
        </div>
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-6">
          {tabs.map(([key, label, count]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-200 ${
                tab === key ? "border-forest text-forest" : "border-transparent text-graphite-soft hover:text-graphite"
              }`}
              data-testid={`admin-tab-${key}`}
            >
              {label}
              {count !== null && <span className="ml-1 text-graphite/40">({count})</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10">
        {tab === "dashboard" && <DashboardTab go={setTab} />}

        {tab === "submissions" && (
          <div>
            <div className="mb-8 flex flex-wrap gap-2">
              <button
                onClick={() => setFilter("all")}
                className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${filter === "all" ? "border-forest bg-forest text-white" : "border-graphite/20 text-graphite-soft hover:border-forest"}`}
                data-testid="admin-filter-all"
              >
                {t("admin.all")}
              </button>
              {FORM_TYPES.map((ft) => (
                <button
                  key={ft}
                  onClick={() => setFilter(ft)}
                  className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${filter === ft ? "border-forest bg-forest text-white" : "border-graphite/20 text-graphite-soft hover:border-forest"}`}
                  data-testid={`admin-filter-${ft}`}
                >
                  {t(`adminTypes.${ft}`)}
                </button>
              ))}
            </div>
            {filteredSubs.length === 0 ? (
              <div className="flex flex-col items-center gap-4 border border-dashed border-graphite/20 py-20 text-graphite-soft" data-testid="admin-submissions-empty">
                <Inbox className="h-8 w-8" />
                <p className="text-sm">{t("admin.submissionsEmpty")}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredSubs.map((s) => (
                  <article key={s.id} className="border border-graphite/10 bg-white p-6" data-testid={`admin-submission-${s.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <span className="inline-block bg-forest-soft px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-forest">
                          {t(`adminTypes.${s.type}`)}
                        </span>
                        <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-graphite">{s.name}</h3>
                        <a href={`mailto:${s.email}`} className="text-sm text-science underline underline-offset-4">
                          {s.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-graphite-soft">
                          {t("admin.receivedOn")} {fmtDate(s.created_at)}
                        </span>
                        <button
                          onClick={() => del("submissions", s.id)}
                          aria-label={t("admin.delete")}
                          className="text-graphite/30 transition-colors hover:text-destructive"
                          data-testid={`admin-submission-delete-${s.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {s.details && Object.keys(s.details).length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-1 border-t border-graphite/10 pt-4">
                        {Object.entries(s.details).map(([k, v]) => (
                          <p key={k} className="text-xs text-graphite-soft">
                            <span className="font-mono uppercase tracking-wide">{k} :</span> {v}
                          </p>
                        ))}
                      </div>
                    )}
                    <p className="mt-4 whitespace-pre-wrap border-l-2 border-forest/30 pl-4 text-sm leading-relaxed text-graphite">{s.message}</p>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "news" && <NewsTab />}

        {tab === "events" && <EventsTab />}

        {tab === "projects" && <ProjectsTab />}

        {tab === "newsletter" && <NewsletterTab />}

        {tab === "docs" && (
          <div className="grid gap-10 lg:grid-cols-5">
            <form onSubmit={submitDoc} className="space-y-5 border border-graphite/10 bg-white p-6 lg:col-span-2" data-testid="admin-doc-form">
              <h2 className="font-display text-xl font-bold tracking-tight text-graphite">{t("admin.docNew")}</h2>
              <div>
                <label className={labelCls}>{t("admin.titleFr")}</label>
                <input required className={inputCls} value={docForm.title_fr} onChange={(e) => setDocForm({ ...docForm, title_fr: e.target.value })} data-testid="admin-doc-title-fr" />
              </div>
              <div>
                <label className={labelCls}>{t("admin.titleEn")}</label>
                <input className={inputCls} value={docForm.title_en} onChange={(e) => setDocForm({ ...docForm, title_en: e.target.value })} data-testid="admin-doc-title-en" />
              </div>
              <div>
                <label className={labelCls}>{t("admin.category")}</label>
                <select className={inputCls} value={docForm.category} onChange={(e) => setDocForm({ ...docForm, category: e.target.value })} data-testid="admin-doc-category">
                  {docKeys.map((k, i) => (
                    <option key={k} value={k}>
                      {docCats[i] ? docCats[i].title : k}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>{t("admin.descFr")}</label>
                <textarea rows={2} className={inputCls} value={docForm.description_fr} onChange={(e) => setDocForm({ ...docForm, description_fr: e.target.value })} data-testid="admin-doc-desc-fr" />
              </div>
              <div>
                <label className={labelCls}>{t("admin.descEn")}</label>
                <textarea rows={2} className={inputCls} value={docForm.description_en} onChange={(e) => setDocForm({ ...docForm, description_en: e.target.value })} data-testid="admin-doc-desc-en" />
              </div>
              <div>
                <label className={labelCls}>{t("admin.file")}</label>
                <input
                  type="file"
                  required
                  accept=".pdf,.doc,.docx,.odt,.txt"
                  onChange={(e) => setFile(e.target.files[0] || null)}
                  className="w-full border border-dashed border-graphite/25 bg-white px-4 py-6 text-sm text-graphite-soft file:mr-4 file:border-0 file:bg-forest file:px-4 file:py-2 file:font-mono file:text-[11px] file:uppercase file:tracking-[0.15em] file:text-white"
                  data-testid="admin-doc-file"
                />
              </div>
              <button
                type="submit"
                disabled={saving || !file}
                className="w-full bg-forest px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-graphite disabled:opacity-50"
                data-testid="admin-doc-submit"
              >
                {saving ? t("forms.sending") : t("admin.upload")}
              </button>
            </form>
            <div className="space-y-4 lg:col-span-3">
              {docs.length === 0 && <p className="border border-dashed border-graphite/20 py-16 text-center text-sm text-graphite-soft">{t("admin.empty")}</p>}
              {docs.map((d) => (
                <article key={d.id} className="flex items-center justify-between gap-4 border border-graphite/10 bg-white p-6" data-testid={`admin-doc-${d.id}`}>
                  <div className="flex items-start gap-4">
                    <FileText className="mt-1 h-5 w-5 shrink-0 text-forest" />
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-forest">{d.category} — {fmtDate(d.created_at)}</p>
                      <h3 className="mt-1 font-display text-lg font-bold tracking-tight text-graphite">{d.title_fr}</h3>
                      <p className="mt-1 text-xs text-graphite-soft">
                        {d.filename} · {(d.size / 1024).toFixed(0)} Ko
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <a href={`${API}/documents/${d.id}/file`} target="_blank" rel="noreferrer" aria-label={t("admin.download")} className="text-graphite/40 transition-colors hover:text-forest" data-testid={`admin-doc-download-${d.id}`}>
                      <Download className="h-4 w-4" />
                    </a>
                    <button onClick={() => del("documents", d.id)} aria-label={t("admin.delete")} className="text-graphite/30 transition-colors hover:text-destructive" data-testid={`admin-doc-delete-${d.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
